/**
 * Trésorerie : encaissements et décaissements par mois, avec projection.
 *
 * Le passé se lit dans les paiements et les dépenses réels. L'avenir se
 * projette depuis les factures émises non soldées, positionnées sur leur mois
 * d'échéance — c'est une prévision, pas une certitude, et l'interface doit le
 * dire.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import { round2 } from "../../money.js";

export interface CashflowPoint {
  /** Mois au format AAAA-MM. */
  month: string;
  income: number;
  expense: number;
  /** Encaissé moins décaissé sur le mois. */
  net: number;
  /** Cumul depuis le début de la fenêtre. */
  balance: number;
  isProjection: boolean;
}

export interface CashflowEntry {
  date: string;
  amount: number;
}

export interface TreasuryRepository {
  paymentsSince(ctx: ExecutionContext, from: string): Promise<CashflowEntry[]>;
  expensesSince(ctx: ExecutionContext, from: string): Promise<CashflowEntry[]>;
  /** Factures émises non soldées, datées de leur échéance. */
  expectedIncomeSince(ctx: ExecutionContext, from: string): Promise<CashflowEntry[]>;
}

export const cashflowInput = z.object({
  confirmationId: z.string().optional(),
  monthsBack: z.coerce.number().int().min(1).max(24).default(6),
  monthsForward: z.coerce.number().int().min(0).max(12).default(3),
});

export type CashflowInput = z.infer<typeof cashflowInput>;

/**
 * Clé de mois d'une date locale.
 *
 * Surtout pas `toISOString()` sur une date construite localement : à Paris, le
 * 1er du mois à 00 h devient le dernier jour du mois précédent en UTC, et tous
 * les encaissements du premier jour basculaient dans le mauvais mois.
 */
const monthKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export function buildCashflow(
  payments: readonly CashflowEntry[],
  expenses: readonly CashflowEntry[],
  expected: readonly CashflowEntry[],
  monthsBack: number,
  monthsForward: number,
  today: Date = new Date(),
): CashflowPoint[] {
  const buckets = new Map<string, { income: number; expense: number; isProjection: boolean }>();

  for (let i = -monthsBack + 1; i <= monthsForward; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    buckets.set(monthKey(d), { income: 0, expense: 0, isProjection: i > 0 });
  }

  const add = (entries: readonly CashflowEntry[], field: "income" | "expense") => {
    for (const entry of entries) {
      // Les dates viennent de la base en AAAA-MM-JJ : on tronque plutôt que de
      // les reparser, ce qui réintroduirait le décalage de fuseau.
      const bucket = buckets.get(entry.date.slice(0, 7));
      if (bucket) bucket[field] += entry.amount;
    }
  };

  add(payments, "income");
  add(expenses, "expense");

  // Les encaissements attendus ne comptent que sur les mois à venir : les
  // ajouter au passé ferait apparaître de l'argent qui n'est jamais rentré.
  for (const entry of expected) {
    const bucket = buckets.get(entry.date.slice(0, 7));
    if (bucket?.isProjection) bucket.income += entry.amount;
  }

  let balance = 0;
  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, b]) => {
      const net = b.income - b.expense;
      balance += net;
      return {
        month,
        income: round2(b.income),
        expense: round2(b.expense),
        net: round2(net),
        balance: round2(balance),
        isProjection: b.isProjection,
      };
    });
}

export function createTreasuryServices(
  repo: TreasuryRepository,
  now: () => Date = () => new Date(),
) {
  const tresorerie = defineService<CashflowInput, CashflowPoint[]>({
    name: "tresorerie",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const today = now();
      const start = new Date(today.getFullYear(), today.getMonth() - input.monthsBack + 1, 1);
      const from = `${monthKey(start)}-01`;

      const [payments, expenses, expected] = await Promise.all([
        repo.paymentsSince(ctx, from),
        repo.expensesSince(ctx, from),
        repo.expectedIncomeSince(ctx, from),
      ]);

      return buildCashflow(
        payments,
        expenses,
        expected,
        input.monthsBack,
        input.monthsForward,
        today,
      );
    },
  });

  return { tresorerie };
}

export type TreasuryServices = ReturnType<typeof createTreasuryServices>;
